import { gql } from '@apollo/client';
import { MeetingPlacePageHomeMinifiedFragment } from '../fragments/meetingPlacePageHome-minified';
import {
  MeetingPlacePageCoreOfferItemFragment,
  MeetingPlacePageOfferListItemFragment,
  MeetingPlacePageTenantOfferItemFragment,
} from '../fragments/meetingPlacePageOfferItem';
import { fetchAndMergeOfferItemRichText } from './meetingPlacePageOfferItemRichTextQuery';

export const MEETING_PLACE_PAGE_OFFER_CORE_QUERY = gql`
  ${MeetingPlacePageCoreOfferItemFragment}
  query MeetingPlacePageOfferItemQuery($id: String!, $language: String!) {
    meetingPlacePageOffer(id: $id, locale: $language) {
      ...MeetingPlacePageCoreOfferItem
    }
  }
`;

export const MEETING_PLACE_PAGE_OFFER_CORE_SEO_QUERY = gql`
  query MeetingPlacePageOfferItemSeoQuery(
    $id: String!
    $locale: String!
    $preview: Boolean
  ) {
    meetingPlacePageOffer(id: $id, locale: $locale, preview: $preview) {
      sys {
        id
        locale
      }
      slug
      name
      seoDescription
      coverImage {
        url
        width
        height
      }
      body {
        json
      }
      typeOfOffer {
        name
      }
      tenant {
        ... on _Node {
          ... on MeetingPlacePageTenantShop {
            brand {
              ... on LibraryBrandShop {
                logo {
                  url
                  height
                  width
                }
              }
              ... on LibraryBrandFoodBeverage {
                logo {
                  url
                  height
                  width
                }
              }
              ... on LibraryBrandEntertainment {
                logo {
                  url
                  height
                  width
                }
              }
            }
            name
            logo {
              url
              width
              height
            }
          }
          ... on MeetingPlacePageTenantEntertainment {
            brand {
              ... on LibraryBrandShop {
                logo {
                  url
                  width
                  height
                }
              }
              ... on LibraryBrandFoodBeverage {
                logo {
                  url
                  width
                  height
                }
              }
              ... on LibraryBrandEntertainment {
                logo {
                  url
                  width
                  height
                }
              }
            }
            name
            logo {
              url
              width
              height
            }
          }
          ... on MeetingPlacePageTenantFoodBeverage {
            brand {
              ... on LibraryBrandShop {
                logo {
                  url
                  width
                  height
                }
              }
              ... on LibraryBrandFoodBeverage {
                logo {
                  url
                  width
                  height
                }
              }
              ... on LibraryBrandEntertainment {
                logo {
                  url
                  width
                  height
                }
              }
            }
            name
            logo {
              url
              width
              height
            }
          }
        }
      }
    }
  }
`;

export const MEETING_PLACE_PAGE_OFFER_TENANT_QUERY = gql`
  ${MeetingPlacePageTenantOfferItemFragment}
  query MeetingPlacePageOfferItemQuery($id: String!, $language: String!) {
    meetingPlacePageOffer(id: $id, locale: $language) {
      ...MeetingPlacePageTenantOfferItem
    }
  }
`;

export const MEETING_PLACE_PAGE_HOME_MINIFIED_HOME_QUERY = gql`
  ${MeetingPlacePageHomeMinifiedFragment}
  query MeetingPlacePageHomeQuery($tagId: String!, $locale: String!) {
    meetingPlacePageHomeCollection(
      where: { contentfulMetadata: { tags: { id_contains_some: [$tagId] } } }
      locale: $locale
    ) {
      items {
        ...MeetingPlacePageHomeMinified
      }
    }
  }
`;

export const GET_MATCHING_OFFERS = gql`
  query GetMatchingOffers($ids: [String!]!, $language: String!) {
    meetingPlacePageOfferCollection(
      where: { sys: { id_in: $ids } }
      locale: $language
    ) {
      items {
        __typename
        sys {
          id
        }
        name
        caption
        validFrom
        validTo
        slug
        coverImage {
          url
          title
          description
          width
          height
        }
        typeOfOffer {
          name
          internalName
        }
        tags
      }
    }
  }
`;

export const GET_ACTIVE_OFFERS = gql`
  ${MeetingPlacePageOfferListItemFragment}
  query GetActiveOffers($ids: [String!]!, $language: String!, $limit: Int) {
    meetingPlacePageOfferCollection(
      where: { sys: { id_in: $ids } }
      locale: $language
      limit: $limit
    ) {
      items {
        ...MeetingPlacePageOfferListItem
      }
    }
  }
`;
/**
 * Fetches offer page data with rich text body merged
 * Combines core and tenant data, then merges rich text
 */
export async function getMeetingPlacePageOfferData(
  id: string,
  locale: string,
  preview?: boolean,
) {
  const { getApolloClient } = await import('../apolloClient');
  const client = getApolloClient(preview);

  const [coreRes, tenantRes] = await Promise.all([
    client.query({
      query: MEETING_PLACE_PAGE_OFFER_CORE_QUERY,
      variables: {
        id,
        language: locale,
      },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    }),
    client.query({
      query: MEETING_PLACE_PAGE_OFFER_TENANT_QUERY,
      variables: {
        id,
        language: locale,
      },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    }),
  ]);

  const coreOffer = (coreRes?.data as any)?.meetingPlacePageOffer;
  const tenantData = (tenantRes?.data as any)?.meetingPlacePageOffer?.tenant;

  if (!coreOffer) {
    return null;
  }

  // Combine core and tenant data
  const combinedOffer = {
    ...coreOffer,
    tenant: tenantData,
  };

  // Fetch and merge rich text body for the offer item
  const mergedOfferData = await fetchAndMergeOfferItemRichText(
    client,
    combinedOffer,
    locale,
    preview,
  );

  return mergedOfferData;
}
